import { useLingui } from '@lingui/react/macro'
import {
  Badge,
  Callout,
  Hint,
  num,
  Panel,
  PanelTitle,
  percent,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import {
  type BandCoverageVerdict,
  bandCoverageVerdict,
} from 'features/sentinelDesign/runResult'
import type { SentinelRunResult } from 'features/sentinelDesign/types'
import { CollapsibleSection } from 'features/timeSeries/experiment/CollapsibleSection'
import { DiagnosticsPanel } from 'features/timeSeries/experiment/DiagnosticsPanel'
import { METHOD_LABELS } from 'features/timeSeries/experiment/knobCopy'
import { ResidualMiniPlot } from 'features/timeSeries/experiment/ResidualMiniPlot'
import { FC, useState } from 'react'
import styled from 'styled-components'

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(24rem, 1fr));
  gap: 0.75rem;
`

const MethodCard = styled.div<{ $low: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.65rem;
  border: 1px solid
    ${({ theme, $low }) => ($low ? '#f97316' : theme.forecast.border)};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.background2};
  min-width: 0;
`

const MethodName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
`

/** The band-calibration chip, plus the sentence explaining what it means. */
const BandVerdict: FC<{ verdict: BandCoverageVerdict }> = ({ verdict }) => {
  if (verdict.level === 'low') {
    return (
      <Callout $tone='warning'>
        <strong>Band under-covers.</strong> Held-out rows fall inside the ±band
        only {percent(verdict.measured, 1)} of the time against a nominal{' '}
        {percent(verdict.expected)}
        {verdict.flaggedByBackend ? ', flagged by the backend' : ''}. Residual
        excursions from this method are mostly the band being too tight, not the
        asset moving — read it alongside the other methods rather than on its
        own.
        {verdict.note ? ` ${verdict.note}` : ''}
      </Callout>
    )
  }
  if (verdict.level === 'unknown') {
    return (
      <Hint>
        No held-out band coverage was reported, so this method&apos;s
        calibration is unverified.
      </Hint>
    )
  }
  return null
}

export interface RunResultsPanelProps {
  run: SentinelRunResult
}

/**
 * The fitted run: every method that was fitted, its held-out residual plot and
 * its metrics, with a method whose band does not hold marked as such.
 *
 * All four methods are always fitted and they are NOT equally trustworthy —
 * `target_mlp_driver_own` (the self-history variant) has measured band
 * exceedance far above its nominal 5% — so presenting them as a uniform grid of
 * results would be misleading. A flagged method keeps its plot but carries the
 * caveat next to it.
 */
export const RunResultsPanel: FC<RunResultsPanelProps> = ({ run }) => {
  const { i18n } = useLingui()
  const methodKeys = Object.keys(run.methods)
  const [selected, setSelected] = useState(methodKeys[0] ?? '')
  const lowCount = methodKeys.filter(
    (key) => bandCoverageVerdict(run.methods[key]).level === 'low'
  ).length

  return (
    <Panel aria-label='Run results'>
      <Row>
        <PanelTitle>Run results</PanelTitle>
        <Badge>{methodKeys.length} methods fitted</Badge>
        {lowCount > 0 && (
          <Badge $tone='warning'>{lowCount} with an under-covering band</Badge>
        )}
      </Row>
      <Hint>Target: {run.target || 'unreported'}</Hint>

      <MethodGrid>
        {methodKeys.map((key) => {
          const method = run.methods[key]
          const verdict = bandCoverageVerdict(method)
          const test = method.metrics_by_role?.test
          return (
            <MethodCard key={key} $low={verdict.level === 'low'}>
              <Row>
                <MethodName>
                  {method.label ||
                    (METHOD_LABELS[key] ? i18n._(METHOD_LABELS[key]) : key)}
                </MethodName>
                {verdict.level === 'low' && (
                  <Badge $tone='warning'>band under-covers</Badge>
                )}
                {verdict.level === 'ok' && verdict.measured !== null && (
                  <Badge>band {percent(verdict.measured, 1)}</Badge>
                )}
              </Row>
              <ResidualMiniPlot result={run} methodKey={key} />
              <BandVerdict verdict={verdict} />
              <StatTable>
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Rows</th>
                    <th>MAE</th>
                    <th>RMSE</th>
                    <th>R²</th>
                    <th>Band cov.</th>
                  </tr>
                </thead>
                <tbody>
                  {(['train', 'val', 'test'] as const).map((role) => {
                    const metrics = method.metrics_by_role?.[role]
                    if (!metrics) return null
                    return (
                      <tr key={role}>
                        <td>{role}</td>
                        <td>{metrics.rows}</td>
                        <td>{num(metrics.mae, 4)}</td>
                        <td>{num(metrics.rmse, 4)}</td>
                        <td>{num(metrics.r2, 3)}</td>
                        <td>{percent(metrics.band_coverage, 1)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </StatTable>
              {!test && (
                <Hint>
                  No test-split metrics — the split left no held-out rows for
                  this method.
                </Hint>
              )}
            </MethodCard>
          )
        })}
      </MethodGrid>

      {methodKeys.length > 0 && (
        <CollapsibleSection
          sectionKey='sentinel-run-diagnostics'
          title='Diagnostics for one method'
          defaultOpen={false}>
          <DiagnosticsPanel
            result={run}
            methodKey={selected || methodKeys[0]}
            onMethodKeyChange={setSelected}
            title='Fitted diagnostics'
          />
        </CollapsibleSection>
      )}
    </Panel>
  )
}
